import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import {
  ReportContextType,
  ReportGenerationResponse,
  ReportMetadata,
  ScheduledReport,
  ReportGenerationRequest,
  ReportExportOptions
} from '../types';
import * as reportsApi from '../api/reportsApi';

interface ReportsState {
  currentReport: ReportGenerationResponse | null;
  reports: ReportMetadata[];
  scheduledReports: ScheduledReport[];
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
  lastGeneratedReport: ReportGenerationResponse | null;
}

type ReportsAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SUCCESS_MESSAGE'; payload: string | null }
  | { type: 'SET_CURRENT_REPORT'; payload: ReportGenerationResponse | null }
  | {
      type: 'SET_LAST_GENERATED_REPORT';
      payload: ReportGenerationResponse | null;
    }
  | { type: 'SET_REPORTS'; payload: ReportMetadata[] }
  | { type: 'SET_SCHEDULED_REPORTS'; payload: ScheduledReport[] }
  | { type: 'ADD_SCHEDULED_REPORT'; payload: ScheduledReport }
  | {
      type: 'UPDATE_SCHEDULED_REPORT';
      payload: { id: string; report: ScheduledReport };
    }
  | { type: 'DELETE_SCHEDULED_REPORT'; payload: string };

const initialState: ReportsState = {
  currentReport: null,
  reports: [],
  scheduledReports: [],
  isLoading: false,
  error: null,
  successMessage: null,
  lastGeneratedReport: null
};

function reportsReducer(
  state: ReportsState,
  action: ReportsAction
): ReportsState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        successMessage: null
      };
    case 'SET_SUCCESS_MESSAGE':
      return { ...state, successMessage: action.payload, error: null };
    case 'SET_CURRENT_REPORT':
      return { ...state, currentReport: action.payload };
    case 'SET_LAST_GENERATED_REPORT':
      return { ...state, lastGeneratedReport: action.payload };
    case 'SET_REPORTS':
      return { ...state, reports: action.payload };
    case 'SET_SCHEDULED_REPORTS':
      return { ...state, scheduledReports: action.payload };
    case 'ADD_SCHEDULED_REPORT':
      return {
        ...state,
        scheduledReports: [...state.scheduledReports, action.payload]
      };
    case 'UPDATE_SCHEDULED_REPORT':
      return {
        ...state,
        scheduledReports: state.scheduledReports.map((report) =>
          report.id === action.payload.id ? action.payload.report : report
        )
      };
    case 'DELETE_SCHEDULED_REPORT':
      return {
        ...state,
        scheduledReports: state.scheduledReports.filter(
          (report) => report.id !== action.payload
        )
      };
    default:
      return state;
  }
}

const ReportsContext = createContext<ReportContextType | undefined>(undefined);

interface ReportsProviderProps {
  children: ReactNode;
}

export function ReportsProvider({ children }: ReportsProviderProps) {
  const [state, dispatch] = useReducer(reportsReducer, initialState);

  const generateReport = async (
    request: ReportGenerationRequest
  ): Promise<ReportGenerationResponse | undefined> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      dispatch({ type: 'SET_SUCCESS_MESSAGE', payload: null });

      const response = await reportsApi.generateReport(request);
      dispatch({ type: 'SET_CURRENT_REPORT', payload: response });
      dispatch({ type: 'SET_LAST_GENERATED_REPORT', payload: response });

      // Show success message
      const reportTypeLabel = request.type
        .replace('-', ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
      const successMsg = `${reportTypeLabel} report generation started! Report ID: ${response.reportId}`;
      dispatch({ type: 'SET_SUCCESS_MESSAGE', payload: successMsg });

      // Start polling for report status if the report is processing
      if (response.status === 'processing') {
        pollReportStatus(response.reportId);
      }

      // Refresh the reports list after a short delay to allow backend processing
      setTimeout(() => {
        loadReports();
      }, 1000);

      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to generate report';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return undefined;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const pollReportStatus = async (reportId: string): Promise<void> => {
    const maxAttempts = 30; // Poll for up to 30 attempts (30 seconds)
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const statusResponse = await reportsApi.getReportStatus(reportId);

        // Update the current report status if it's the same report
        if (state.currentReport?.reportId === reportId) {
          dispatch({ type: 'SET_CURRENT_REPORT', payload: statusResponse });
        }

        // If report is completed or failed, stop polling and refresh reports list
        if (
          statusResponse.status === 'completed' ||
          statusResponse.status === 'failed'
        ) {
          loadReports(); // Refresh the reports list to show updated status

          if (statusResponse.status === 'completed') {
            const reportTypeLabel =
              statusResponse.metadata?.type
                ?.replace('-', ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Report';
            dispatch({
              type: 'SET_SUCCESS_MESSAGE',
              payload: `${reportTypeLabel} completed successfully and is ready for download!`
            });
          } else {
            dispatch({
              type: 'SET_ERROR',
              payload: 'Report generation failed. Please try again.'
            });
          }

          return; // Stop polling
        }

        // Continue polling if still processing and under max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000); // Poll every 1 second
        } else {
          console.warn('Report status polling stopped after maximum attempts');
          loadReports(); // Final refresh even if polling timed out
        }
      } catch (error) {
        console.error('Error polling report status:', error);
        // Continue polling even if there's an error, unless max attempts reached
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds on error
        }
      }
    };

    // Start polling after a 1 second delay
    setTimeout(poll, 1000);
  };

  const exportReport = async (
    reportId: string,
    options: ReportExportOptions
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const blob = await reportsApi.exportReport(reportId, options);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on format
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `report_${reportId}_${timestamp}.${options.format}`;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to export report';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const createScheduledReport = async (
    report: Omit<ScheduledReport, 'id'>
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const newReport = await reportsApi.createScheduledReport(report);
      dispatch({ type: 'ADD_SCHEDULED_REPORT', payload: newReport });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create scheduled report';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateScheduledReport = async (
    id: string,
    report: Partial<ScheduledReport>
  ): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const updatedReport = await reportsApi.updateScheduledReport(id, report);
      dispatch({
        type: 'UPDATE_SCHEDULED_REPORT',
        payload: { id, report: updatedReport }
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update scheduled report';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteScheduledReport = async (id: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      await reportsApi.deleteScheduledReport(id);
      dispatch({ type: 'DELETE_SCHEDULED_REPORT', payload: id });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to delete scheduled report';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadReports = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const [reports, scheduledReports] = await Promise.all([
        reportsApi.fetchReports(),
        reportsApi.fetchScheduledReports()
      ]);

      dispatch({ type: 'SET_REPORTS', payload: reports });
      dispatch({ type: 'SET_SCHEDULED_REPORTS', payload: scheduledReports });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load reports';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const clearSuccess = (): void => {
    dispatch({ type: 'SET_SUCCESS_MESSAGE', payload: null });
  };

  const contextValue: ReportContextType = {
    currentReport: state.currentReport,
    reports: state.reports,
    scheduledReports: state.scheduledReports,
    isLoading: state.isLoading,
    error: state.error,
    successMessage: state.successMessage,
    lastGeneratedReport: state.lastGeneratedReport,
    generateReport,
    exportReport,
    createScheduledReport,
    updateScheduledReport,
    deleteScheduledReport,
    loadReports,
    clearError,
    clearSuccess
  };

  return (
    <ReportsContext.Provider value={contextValue}>
      {children}
    </ReportsContext.Provider>
  );
}

export function useReports(): ReportContextType {
  const context = useContext(ReportsContext);
  if (context === undefined) {
    throw new Error('useReports must be used within a ReportsProvider');
  }
  return context;
}

export default ReportsContext;
